import { Injectable } from '@angular/core';
import { PopoverController, PopoverOptions } from '@ionic/angular';
import { Observable, from, map, switchMap, tap, take } from 'rxjs';
import { ProductDescriptionComponent } from '../components/product-description/product-description.component';
import { InStoreProduct, Product } from '../api/contracts/product';
import { ForgotPasswordConfirmationComponent } from '../components/forgot-password-confirmation/forgot-password-confirmation.component';
import { InStoreProductsComponent } from '../pages/in-store-products/in-store-products.component';
import { OrderType } from '../api/contracts/order';
import { OrderTypeSwitchWarningComponent } from '../components/order-type-switch-warning/order-type-switch-warning.component';
import { NoCreditCardAddedComponent } from '../components/no-credit-card-added/no-credit-card-added.component';
import { SubsidyInfoModalComponent } from '../components/subsidy-info-modal/subsidy-info-modal.component';
import { OrderConfirmationScannerComponent } from '../pages/order-confirmation-scanner/order-confirmation-scanner.component';
import { GenericModalOptions, GenericModalType } from '../types/modals';
import { GenericInfoPopupComponent } from '../components/generic-info-popup/generic-info-popup.component';
import { SubsidyCodeActivationComponent } from '../components/subsidy-code-activation/subsidy-code-activation.component';
import { DeleteProfileComponent } from '../components/delete-profile/delete-profile.component';
import { ManualBarcodeInputModalComponent } from '../components/manual-barcode-input-modal/manual-barcode-input-modal.component';
import { ProductSearchModalComponent } from '../components/product-search-modal/product-search-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(private readonly popoverCtrl: PopoverController) {}

  private presentModal<T>(
    component: any,
    inputData?: Record<string, any>,
    props?: Partial<PopoverOptions>
  ): Observable<T> {
    return from(
      this.popoverCtrl.create({
        component: component,
        componentProps: inputData,
        showBackdrop: true,
        backdropDismiss: true,
        mode: 'md',
        cssClass: props?.cssClass,
      })
    ).pipe(
      tap((modal) => modal.present()),
      switchMap((modal) =>
        from(modal.onDidDismiss()).pipe(map(({ data, role }) => data as T))
      )
    );
  }

  dismiss<T>(data?: T) {
    return this.popoverCtrl.dismiss(data);
  }

  getTop() {
    return this.popoverCtrl.getTop();
  }

  showGenericInfoPopup(options: GenericModalOptions): Observable<void> {
    return this.presentModal<void>(
      GenericInfoPopupComponent,
      {
        header: options.header,
        message: options.message,
        type: options?.type || GenericModalType.INFO,
      },
      {
        cssClass: 'primary-2-backdrop',
      }
    );
  }

  showProductDescriptionModal<T>(product: Product): Observable<T> {
    return this.presentModal<T>(ProductDescriptionComponent, { product });
  }

  showForgotPasswordConfirmationModal<T>(email: string): Observable<T> {
    return this.presentModal<T>(
      ForgotPasswordConfirmationComponent,
      { email },
      {
        cssClass: 'primary-backdrop',
      }
    );
  }

  showNewProductsPopup(products: InStoreProduct[]): Observable<unknown> {
    return this.presentModal(
      InStoreProductsComponent,
      { products, isOpenedFromModal: true },
      {
        cssClass: 'primary-2-backdrop',
      }
    );
  }

  showOrderTypeSwitchWarningPopup(
    itemsQuantity: number,
    desiredType: OrderType
  ): Observable<boolean> {
    return this.presentModal<boolean>(
      OrderTypeSwitchWarningComponent,
      { itemsQuantity, desiredType },
      {
        cssClass: 'primary-2-backdrop',
      }
    );
  }

  showNoCreditCardAddedModal(): Observable<boolean> {
    return this.presentModal<boolean>(NoCreditCardAddedComponent, {}, {});
  }

  showSubsidyInfoModal(subsidyAmount: number): Observable<boolean> {
    return this.presentModal<boolean>(
      SubsidyInfoModalComponent,
      { subsidyAmount },
      {
        cssClass: 'primary-2-backdrop',
      }
    );
  }

  showPickupConfirmationScannerModal(barcode: string): Observable<boolean> {
    return this.presentModal(
      OrderConfirmationScannerComponent,
      { barcodeToMatch: barcode },
      {}
    );
  }

  showSubsidyCodeActivationModal(): Observable<boolean> {
    return this.presentModal(SubsidyCodeActivationComponent, {}, {});
  }

  showDeleteProfileModal(): Observable<boolean> {
    return this.presentModal(DeleteProfileComponent, {}, {});
  }

  showManualBarcodeInputModal(): Observable<string | null> {
    return this.presentModal(ManualBarcodeInputModalComponent, {}, {});
  }

  showProductSearchModal(locationHash: string): Observable<Product | null> {
    return this.presentModal(
      ProductSearchModalComponent,
      {
        locationHash,
      },
      {}
    );
  }
}
